/**
 * Input validation utilities for API parameters
 */

// UUID v4 pattern (used for job IDs, trace IDs, etc.)
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// General ID pattern (allows UUIDs, ULIDs, and other common ID formats including dots)
const ID_PATTERN = /^[a-zA-Z0-9_.-]{1,128}$/;

// Tenant ID pattern (alphanumeric with hyphens and underscores)
const TENANT_ID_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;

// Namespace pattern (alphanumeric with dots, hyphens, and underscores)
const NAMESPACE_PATTERN = /^[a-zA-Z0-9._-]{1,128}$/;

// Time range values
const VALID_TIME_RANGES = ['1h', '6h', '12h', '24h', '7d', '30d'];

// Job statuses (raw event statuses)
const VALID_JOB_STATUSES = [
  'JOB_START',
  'PROCESS_STEP_START',
  'PROCESS_STEP_END',
  'JOB_END',
  'JOB_END_EXCEPTION',
];

// Rolled-up job statuses (for filtered job lists)
const VALID_ROLLED_UP_STATUSES = ['active', 'completed', 'failed'];

// Log levels
const VALID_LOG_LEVELS = ['DEBUG', 'INFO', 'WARN', 'ERROR', 'FATAL'];

// Table types
const VALID_TABLE_TYPES = ['job_status', 'stat', 'log'];

export function isValidUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

export function isValidId(value: string): boolean {
  return ID_PATTERN.test(value);
}

export function isValidTenantId(value: string): boolean {
  return TENANT_ID_PATTERN.test(value);
}

export function isValidNamespace(value: string): boolean {
  return NAMESPACE_PATTERN.test(value);
}

export function isValidTimeRange(value: string): boolean {
  return VALID_TIME_RANGES.includes(value);
}

export function isValidJobStatus(value: string): boolean {
  return VALID_JOB_STATUSES.includes(value) || VALID_ROLLED_UP_STATUSES.includes(value);
}

export function isValidLogLevel(value: string): boolean {
  return VALID_LOG_LEVELS.includes(value);
}

export function isValidTableType(value: string): boolean {
  return VALID_TABLE_TYPES.includes(value);
}

export function isValidPage(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 10000;
}

export function isValidPageSize(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 100;
}

/**
 * Sanitize a string for safe display (prevents XSS)
 */
export function sanitizeString(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * Validate and sanitize search query
 */
export function sanitizeSearchQuery(query: string): string {
  // Remove any characters that aren't allowed in IDs
  return query.trim().slice(0, 128);
}

/**
 * Validate API parameters and throw if invalid
 */
export function validateApiParams(params: object): void {
  const p = params as Record<string, unknown>;

  if (p.tenant_id && typeof p.tenant_id === 'string') {
    if (!isValidTenantId(p.tenant_id)) {
      throw new Error('Invalid tenant ID format');
    }
  }

  if (p.time_range && typeof p.time_range === 'string') {
    if (!isValidTimeRange(p.time_range)) {
      throw new Error('Invalid time range');
    }
  }

  if (p.status && typeof p.status === 'string') {
    if (!isValidJobStatus(p.status)) {
      throw new Error('Invalid job status');
    }
  }

  if (p.level && typeof p.level === 'string') {
    if (!isValidLogLevel(p.level)) {
      throw new Error('Invalid log level');
    }
  }

  if (p.table && typeof p.table === 'string') {
    if (!isValidTableType(p.table)) {
      throw new Error('Invalid table type');
    }
  }

  if (p.page && typeof p.page === 'number') {
    if (!isValidPage(p.page)) {
      throw new Error('Invalid page number');
    }
  }

  if (p.page_size && typeof p.page_size === 'number') {
    if (!isValidPageSize(p.page_size)) {
      throw new Error('Invalid page size');
    }
  }

  if (p.namespace && typeof p.namespace === 'string') {
    if (!isValidNamespace(p.namespace)) {
      throw new Error('Invalid namespace format');
    }
  }
}
